<style lang="scss" scoped>
.groups {
	& ::v-deep .el-select {
		width: 100%;
	}
	&__container {
		@include container;
	}
	&__panel {
		@include main-panel;
		& ::v-deep .el-alert {
			padding: 14px;
		}
	}
	&__header {
		@include flex-center;
		&__options {
			margin-left: auto;
		}
	}
	&__footer {
		@include main-panel-footer;
		&__right {
			margin-left: auto;
		}
	}
	&__warning {
		color: red;
		font-size: 20px;
		padding-top: 7px;
	}
}
</style>
<template lang="pug">
.groups
	app-header
		.groups__header(slot='header')
			h1 Match groups
			.groups__header__options
				el-button(type="primary", @click="refetchGroups",  style="margin-right:15px").ml-auto Refetch groups
				el-button(:type="saveButtonType", @click="saveByBatch", :loading="saveLoading", :disabled='multipleMatches || excessiveCreateNows').ml-auto {{ saveText}}
	.groups__container
		.groups__panel
			loading(v-if="$apollo.loading || refetchingGroups || saveLoading")
			template(v-else)
				el-alert(v-if="groupSettings.group_create_mode === 'self'" type="warning" center show-icon :closable="false" title="Group creation is not enabled" :description="`To allow groups to be automatically created during a sync, see Settings > ${providerName} Groups`")
				result-filter.ml-auto.mr-2(v-model="filters" :filter-fields='fields', :remember="true")
				el-table(:data="paginated").custom-table
					el-table-column(prop="name" label="Edusync group")    
						template(slot-scope="scope") 
							span {{ scope.row.name }}
					el-table-column(prop="type" label="Type")
						template(slot-scope="scope") 
							span {{ scope.row.type | capitalize }}
					el-table-column(width="50")
						template(slot-scope="scope")
							.groups__warning(v-if="rowDuplicated(scope.row)")
								i.fe.fe-alert-triangle
							.groups__warning(v-if="tooManyCreateNows(scope.row)")
								i.fe.fe-alert-triangle
					el-table-column(:label="`${providerName} Group`" width="500")
						template(slot-scope="scope")
							el-select(placeholder='Manage' filterable v-model="scope.row.assigned" @change='change', :filter-method="groupsFilter")
								el-option-group
									el-option(key="ignore" value="ignore" label="Ignore")
									el-option(key="create_later" value="create_on_sync" label="Create on sync")
									el-option(key="create" value="create" label="Create now")
								el-option-group(:label="`Match to an existing ${providerName} group`")
									el-option(v-for="group in getTPGroupOptions(scope.row)" :key="group.id" :value="group.id" :label="group.name")
				.groups__footer
					.groups__footer__left
						el-pagination(background='', layout='prev, pager, next', :current-page.sync='page', :page-size='per_page', :total='filtered.length')
					.groups__footer__right                        
						| Showing
						strong &nbsp;{{ paginated.length }}
						| &nbsp;out of a possible
						strong &nbsp;{{ filtered.length }}
						| &nbsp;results

</template>
<script>
import gql from "graphql-tag";
import _ from "lodash";
import Fuse from "fuse.js";
import Bugsnag from '@bugsnag/js';
export default {
	data() {
		return {
			filters: null,
			saveLoading: false,
			refetchingGroups: false,
			page: 1,
			groups: [],
			thirdPartyGroups: [],
			thirdPartyGroupsFiltered: [],
			optionLimit: 20,
			per_page: 50,
			fields: [
				{
					name: "Name",
					path: "name",
					type: "text"
				},
				{
					name: "Type",
					path: "type",
					type: "radio",
					options: [
						{
							label: "House",
							value: "house"
						},
						{
							label: "Registration",
							value: "registration"
						},
						{
							label: "Year",
							value: "year"
						}
					]
				}
			],
			allowedMultipleMatches: ["create_on_sync", "create", "ignore", "Group no longer exists"],
		};
	},

	computed: {
		saveButtonType() {
			return this.multipleMatches ? "danger" : "primary";
		},

		saveText() {
			if (this.multipleMatches) {
				return "Saving is not possible while a group is assigned multiple times";
			}
			return this.saveLoading ? "Saving..." : "Save matches";
		},

		multipleMatches() {
			return _(this.inputGroups)
				.filter({ action: "match" })
				.reject(item => _.includes(["create_on_sync", "create", "ignore", "Group no longer exists"], item.providerId))
				.countBy("providerId")
				.some(qty => qty > 1);
		},

		excessiveCreateNows() {
			return _.filter(this.inputGroups, { providerId: "create" }).length > 3;
		},

		inputGroups() {
			return _(this.groups)
					.reject(item => _.includes([null, "Group no longer exists"], item.assigned))
					.map(group => ({
						wondeId: group.id,
						providerId: group.assigned
					})).value();
		},

		filtered() {
			let filtered = _.map(this.groups, row => {
				const foundRecord = _.find(this.matchedGroups, { wondeId: row.id });
				if (foundRecord) {
					row.assigned = foundRecord.providerId;
					row.status = foundRecord.status;
					if (foundRecord.status === "ignored") {
						row.assigned = "ignore";
					}
					else {
						if (!this.$apollo.loading && !_.isEmpty(this.thirdPartyGroups) && !_.find(this.thirdPartyGroups, item => item.id === foundRecord.providerId)) {
							row.assigned = "Group no longer exists";
						}
					}
				}
				else if (row.type === 'misc' && this.groupSettings.group_create_misc && !this.groupSettings.group_create_misc.length) {
					row.assigned = "ignore";
				}
				else {
					row.assigned = "create_on_sync";
				}
				return row;
			});

			if (this.groups && this.filters) {
				if (this.filters.name) {
					filtered = _.filter(filtered, row => {
						return this.filters.name.filter_type == 'starts_with' ? _.startsWith(row.name.toLowerCase(), this.filters.name.value.toLowerCase()) : row.name.toLowerCase().includes(this.filters.name.value.toLowerCase());
					});
				}
				if (this.filters.type) {
					filtered = _.filter(filtered, { type: this.filters.type.value });
				}
			}
			return filtered;
		},

		paginated() {
			return _.chunk(this.filtered, this.per_page)[this.page - 1] || [];
		},

		groupsLoading() {
			if(this.refetchingGroups) return true;
			return _.isEmpty(this.thirdPartyGroups);
		},
	},

	methods: {
		rowDuplicated(item) {
			return (
				item.assigned &&
				_(this.inputGroups)
					.filter({ providerId: item.assigned })
					.reject(item => _.includes(["create_on_sync", "create", "ignore", "Group no longer exists"], item.providerId))
					.value().length > 1
			);
		},

		tooManyCreateNows(item) {
			return item.assigned && this.excessiveCreateNows && item.assigned === "create";
		},

		change(id) {
			if (this.multipleMatches) {
				this.$message.warning("Notice: This group has been matched multiple times");
			}
			if (this.excessiveCreateNows) {
				this.$message.warning("Notice: Only 3 groups can be created at a time here - to create more you should set them to Create on sync");
			}
		},

		getTPGroupOptions(row) {
			// If thirdPartyGroups is empty or still loading, show matchedGroups
			// filtering out the ones with null providerIds to prevent
			// "Loading Google groups..." as one of the options
			if (_.isEmpty(this.thirdPartyGroups)) {
				return _(this.matchedGroups)
						.filter(cls => {
							return !_.isEmpty(cls.providerId)
						})
						.map(cls => {
							return {
								id: cls.providerId,
								name: cls.providerName ? cls.providerName : "Loading Google groups..."
							}
						})
						.value();
			}

			// The filtered list of third party groups starts with a subset of the full list for better performance
			if (_.isEmpty(this.thirdPartyGroupsFiltered) && !_.isEmpty(this.thirdPartyGroups)) {
				this.thirdPartyGroupsFiltered = this.thirdPartyGroups.slice(0, this.optionLimit);
			}

			// Ensure any groups that have already been assigned are also in the filtered list
			if (!_.includes(this.allowedMultipleMatches, row.assigned)) {
				if (!_.find(this.thirdPartyGroupsFiltered, item => item.id === row.assigned)) {
					// Item is not in filtered list, so let's push it in
					const assignedGroup = _.find(this.thirdPartyGroups, item => item.id === row.assigned);
					if (assignedGroup) {
						this.thirdPartyGroupsFiltered.push(assignedGroup);
					}
				}
			}
			return this.thirdPartyGroupsFiltered;
		},

		groupsFilter(query) {
			const options = {
				keys: ["name"]
			};
			var fuse = new Fuse(this.thirdPartyGroups, options);
			this.thirdPartyGroupsFiltered = fuse.search(query).slice(0, this.optionLimit);
		},

		async saveMatches(input) {
			try {
				await this.$apollo.mutate({
					mutation: gql`
						mutation MatchGroups($input: [MatchGroupInput!]!) {
							MatchGroups(input: $input) {
								id
								type
								name
							}
						}
					`,
					variables: {
						input
					}
				});
			} catch (error) {
				Bugsnag.notify(JSON.stringify(error, null, 2));
				this.$message.error("Failed to save matches");
			}
		},

		async saveByBatch() {
			this.saveLoading = true;

			const perPage = 3;
			let curPage = 1;
			let promises = [];
			let chunk = _.chunk(this.inputGroups, perPage)[curPage - 1];
			
			const shouldRefetch = _.filter(this.inputGroups, (cls) => {
				return ["create"].includes(cls.providerId);
			}).length > 0;
		
			while(chunk) {
				promises.push(this.saveMatches(chunk));
				curPage++;
				chunk = _.chunk(this.inputGroups, perPage)[curPage - 1];
			}

			await Promise.all(promises);
			await this.$apollo.queries.matchedGroups.refetch();
			
			if (shouldRefetch) {
				await this.doRefechGroups();
				this.$message.success("Group matches saved successfully. Please wait while groups are re-fetched");
			} else {
				this.$message.success("Group matches saved successfully");
			}
			
			this.saveLoading = false;
		},

		async refetchGroups() {
			this.$confirm(
					`Re-fetching groups will take some time. Are you sure you want to proceed?`,
					"Warning",
					{
						confirmButtonText: "OK",
						cancelButtonText: "Cancel",
						type: "warning"
					}
			).then(async () => {
				try {
					await this.doRefechGroups();
				} catch (e) {
					this.$message.error("Failed to re-fetch groups");
				}
			})
		},

		async clearGroupCache() {
			await this.$apollo.mutate({
				mutation: gql`
					mutation CacheClear($item: CacheTypeEnum!) {
						CacheClear(item: $item)
					}`,
					variables: {
						item: "groups"
					}
			});
		},

		async doRefechGroups() {
			await this.clearGroupCache();
			this.refetchingGroups = true;
			this.thirdPartyGroups = [];
			await this.$apollo.queries.thirdPartyGroups.refetch();
		}
	},

	apollo: {
		matchedGroups: {
			query: gql`
				{
					MatchedGroups {
						id
						status
						providerId
						wondeId
					}
				}
			`,
			fetchPolicy: "cache-and-network",
			update: r => r.MatchedGroups
		},

		thirdPartyGroups: {
			query: gql`
				{
					ThirdPartyGroups {
						id
						name
					}
				}
			`,
			fetchPolicy: "no-cache",
			update(r) {
				if(r.ThirdPartyGroups && r.ThirdPartyGroups.length > 0) {
					this.refetchingGroups = false;
				}
				return r.ThirdPartyGroups;
			},
			pollInterval: 5000,
			skip() {
				return !this.groupsLoading;
			}
		},

		groups: {
			query: gql`
				{
					Groups {
						id
						name
						type
					}
				}
			`,
			fetchPolicy: "cache-and-network",
			update: r =>
				_(r.Groups)
					.map(group => ({
						...group,
						assigned: null
					}))
					.sortBy(["type", "name"])
					.value()
		},

		groupSettings: {
			query: gql`
				{
					Settings {
						id
						group_create_mode
						group_membership_mode
						group_create_misc {
							id
							name
							type
						}
					}
				}
			`,
			fetchPolicy: "cache-and-network",
			update(r) {
				return _.cloneDeep(r.Settings);
			}
		}
	}
};
</script>
